/* Shared Editorial Calm chrome - tokens, BrandLogo, Nav, Footer, EditorialPage.
 *
 * Used by HomePage and the legal/pricing pages so they share the same look
 * and the navigation stays consistent everywhere.
 */
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useT } from '../../i18n/useT';
import LangSwitcher from './LangSwitcher';

// ─── Tokens ───────────────────────────────────────────────────────────

export const mono = "'JetBrains Mono', monospace";
export const sans = "'Geist', -apple-system, BlinkMacSystemFont, sans-serif";
export const serif = "'Fraunces', 'Times New Roman', serif";
export const hairline = '1px solid rgba(232, 230, 224, 0.08)';
export const hair2 = '1px solid rgba(232, 230, 224, 0.04)';
export const ivory = '#f4f2ee';
export const bodyText = '#d8d6d0';
export const muted = 'rgba(232, 230, 224, 0.55)';
export const subtle = 'rgba(232, 230, 224, 0.28)';

export const QUIZ_PATH = '/quiz/doyourstep-quiz';

// ─── BrandLogo ────────────────────────────────────────────────────────

export const BrandLogo: React.FC<{ height?: number }> = ({ height = 28 }) => (
    <Link
        to="/"
        aria-label="DoYourStep home"
        style={{
            display: 'inline-flex',
            alignItems: 'center',
            color: ivory,
            textDecoration: 'none',
        }}
    >
        <img
            src={`${process.env.PUBLIC_URL || ''}/images/logo-mark.png`}
            alt="DoYourStep"
            style={{ height, width: 'auto', display: 'block' }}
        />
    </Link>
);

// ─── Nav ──────────────────────────────────────────────────────────────

interface NavLink {
    /** i18n key under `nav.*`. */
    labelKey: string;
    href: string;
    /** External anchor link (default false). */
    anchor?: boolean;
}

const DEFAULT_NAV_LINKS: NavLink[] = [
    { labelKey: 'nav.method', href: '/#method', anchor: true },
    { labelKey: 'nav.dimensions', href: '/#dimensions', anchor: true },
    { labelKey: 'nav.research', href: '/#research', anchor: true },
    { labelKey: 'nav.pricing', href: '/pricing' },
];

interface EditorialNavProps {
    links?: NavLink[];
}

export const EditorialNav: React.FC<EditorialNavProps> = ({ links = DEFAULT_NAV_LINKS }) => {
    const navigate = useNavigate();
    const t = useT();

    return (
        <header
            className="lp2__nav"
            style={{
                position: 'sticky',
                top: 0,
                zIndex: 10,
                background: 'rgba(10, 10, 10, 0.94)',
                backdropFilter: 'blur(14px)',
                WebkitBackdropFilter: 'blur(14px)',
                borderBottom: hairline,
                padding: '20px 40px',
            }}
        >
            <div
                className="lp2__nav-row"
                style={{
                    maxWidth: 1320,
                    margin: '0 auto',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <BrandLogo height={28} />
                <div
                    className="lp2__nav-links"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 28,
                        fontSize: 13,
                        color: muted,
                        fontFamily: mono,
                        letterSpacing: '0.06em',
                    }}
                >
                    {links.map((l) =>
                        l.anchor ? (
                            <a key={l.href} href={l.href} style={{ color: 'inherit', textDecoration: 'none' }}>
                                {t(l.labelKey)}
                            </a>
                        ) : (
                            <Link key={l.href} to={l.href} style={{ color: 'inherit', textDecoration: 'none' }}>
                                {t(l.labelKey)}
                            </Link>
                        ),
                    )}
                    <LangSwitcher />
                    <Link
                        to="/login"
                        className="lp2__nav-login"
                        style={{
                            color: ivory,
                            textDecoration: 'none',
                            border: hairline,
                            padding: '10px 18px',
                            borderRadius: 4,
                            fontSize: 12,
                            fontWeight: 500,
                            letterSpacing: '0.08em',
                        }}
                    >
                        {t('nav.login')}
                    </Link>
                    <button
                        type="button"
                        onClick={() => navigate(QUIZ_PATH)}
                        style={{
                            background: '#ef4444',
                            color: ivory,
                            border: 'none',
                            padding: '11px 22px',
                            borderRadius: 4,
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: 'pointer',
                            fontFamily: mono,
                            letterSpacing: '0.08em',
                            boxShadow: '0 0 24px rgba(239, 68, 68, 0.35)',
                        }}
                    >
                        {t('nav.cta')}
                    </button>
                </div>
            </div>
        </header>
    );
};

// ─── Footer ───────────────────────────────────────────────────────────

export const EditorialFooter: React.FC = () => {
    const t = useT();

    return (
        <footer style={{ borderTop: hairline, padding: '40px' }}>
            <div
                className="lp2__footer"
                style={{
                    maxWidth: 1320,
                    margin: '0 auto',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 32,
                    flexWrap: 'wrap',
                }}
            >
                <BrandLogo height={28} />
                <div
                    style={{
                        display: 'flex',
                        gap: 28,
                        fontSize: 11,
                        color: muted,
                        fontFamily: mono,
                        letterSpacing: '0.1em',
                        flexWrap: 'wrap',
                    }}
                >
                    <Link to="/pricing" style={{ color: 'inherit', textDecoration: 'none' }}>
                        {t('footer.pricing')}
                    </Link>
                    <Link to="/terms" style={{ color: 'inherit', textDecoration: 'none' }}>
                        {t('footer.terms')}
                    </Link>
                    <Link to="/privacy" style={{ color: 'inherit', textDecoration: 'none' }}>
                        {t('footer.privacy')}
                    </Link>
                    <Link to="/refund" style={{ color: 'inherit', textDecoration: 'none' }}>
                        {t('footer.refund')}
                    </Link>
                    <Link to="/contact" style={{ color: 'inherit', textDecoration: 'none' }}>
                        {t('footer.contact')}
                    </Link>
                    <span>{t('footer.copyright', { year: new Date().getFullYear() })}</span>
                </div>
            </div>
        </footer>
    );
};

// ─── EditorialPage shell ──────────────────────────────────────────────

interface EditorialPageProps {
    children: React.ReactNode;
}

export const EditorialPage: React.FC<EditorialPageProps> = ({ children }) => (
    <div
        className="lp2"
        style={{
            background: '#0a0a0a',
            color: ivory,
            fontFamily: sans,
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
        }}
    >
        <EditorialNav />
        <main style={{ flex: 1 }}>{children}</main>
        <EditorialFooter />
    </div>
);

// ─── Eyebrow + small helpers ──────────────────────────────────────────

export const Eyebrow: React.FC<{ children: React.ReactNode; color?: string }> = ({ children, color }) => (
    <div
        style={{
            fontSize: 11,
            letterSpacing: '0.24em',
            color: color || muted,
            fontFamily: mono,
            fontWeight: 500,
        }}
    >
        {children}
    </div>
);
